import React from 'react';
import { colors } from '../../../theme';
import { isCellOn } from '../../../utils/straddleMappings';

const CELL_SIZE = 34;

const headerStyle = {
  fontSize: 11,
  fontWeight: 600,
  color: colors.textSecondary,
  whiteSpace: 'nowrap',
  textAlign: 'center',
};

const axisTitleStyle = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: colors.textSecondary,
  whiteSpace: 'nowrap',
};

/**
 * Pair grid shared by the strike and expiration sections.
 *
 * Rows are the call's value and columns the put's, so the diagonal is the even
 * pairing and each triangle is one leg farther or later. Shape toggles fill or
 * clear a whole region; clicking a cell adds or drops that single pair.
 *
 * A CSS grid rather than a <table>: global table rules paint headers white and
 * stretch cells to full width, which would turn these squares into wide bars.
 *
 * @param {Array}    axis          selected values, nearest to farthest
 * @param {Function} labelFor      value -> display label
 * @param {Set}      shapes        shape keys currently on
 * @param {Object}   overrides     one-off cell states from clicks
 * @param {Array}    shapeOptions  [{ key, label, tooltip }]
 * @param {Function} onToggleShape shape key -> void
 * @param {Function} onToggleCell  (callValue, putValue) -> void
 * @param {string}   callAxisTitle e.g. "Call"
 * @param {string}   putAxisTitle  e.g. "Put distance"
 */
function StraddlePairMatrix({
  axis,
  labelFor,
  shapes,
  overrides,
  shapeOptions,
  onToggleShape,
  onToggleCell,
  callAxisTitle,
  putAxisTitle,
}) {
  if (axis.length === 0) return null;

  return (
    <div className="mt-4">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span style={axisTitleStyle}>Pair them</span>
        {shapeOptions.map((shape) => (
          <button
            key={shape.key}
            type="button"
            onClick={() => onToggleShape(shape.key)}
            aria-pressed={shapes.has(shape.key)}
            title={shape.tooltip}
            className={`tab-button ${shapes.has(shape.key) ? 'tab-selected' : 'tab-unselected'}`}
          >
            {shape.label}
          </button>
        ))}
      </div>

      <div className="overflow-x-auto" style={{ textAlign: 'left' }}>
        <div
          style={{
            display: 'inline-grid',
            // Columns grow to fit: a mobile rule gives every button a 44px minimum.
            gridTemplateColumns: `auto repeat(${axis.length}, minmax(${CELL_SIZE}px, max-content))`,
            gap: 3,
            alignItems: 'center',
          }}
        >
          <span style={{ ...axisTitleStyle, paddingRight: 8 }}>{callAxisTitle} ↓</span>
          <span style={{ ...axisTitleStyle, gridColumn: `2 / span ${axis.length}` }}>{putAxisTitle} →</span>

          <span aria-hidden="true" />
          {axis.map((putValue) => (
            <span key={putValue} style={headerStyle}>{labelFor(putValue)}</span>
          ))}

          {axis.map((callValue) => (
            <React.Fragment key={callValue}>
              <span style={{ ...headerStyle, textAlign: 'right', paddingRight: 8 }}>
                {labelFor(callValue)}
              </span>
              {axis.map((putValue) => {
                const on = isCellOn(axis, callValue, putValue, shapes, overrides);
                const description = `Call ${labelFor(callValue)}, put ${labelFor(putValue)}`;
                return (
                  <button
                    key={putValue}
                    type="button"
                    onClick={() => onToggleCell(callValue, putValue)}
                    aria-pressed={on}
                    aria-label={description}
                    title={description}
                    style={{
                      display: 'block',
                      width: CELL_SIZE,
                      height: CELL_SIZE,
                      padding: 0,
                      borderRadius: 6,
                      border: `1px solid ${on ? colors.primary : colors.border}`,
                      background: on ? colors.primary : 'transparent',
                      cursor: 'pointer',
                      transition: 'background 0.15s ease, border-color 0.15s ease',
                    }}
                  />
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

export default StraddlePairMatrix;
